import React, { useState } from 'react'
import type { FolderDto } from '../../Models/Workspace'
import styles from './WorkspaceFolderTree.module.css'

type WorkspaceFolderTreeProps = {
  folders: FolderDto[]
  selectedFolderId: string | null
  draggedDocumentId: string | null
  onSelectFolder: (id: string | null) => void
  onCreateFolder: (name: string, parentId?: string) => void
  onRenameFolder: (id: string, name: string) => void
  onDeleteFolder: (id: string) => void
  onDropDocument: (documentId: string, folderId: string | null) => void
}

type FolderItemProps = {
  folder: FolderDto
  depth: number
  allFolders: FolderDto[]
  selectedFolderId: string | null
  draggedDocumentId: string | null
  dragOverFolderId: string | null
  setDragOverFolderId: (id: string | null) => void
  onSelectFolder: (id: string | null) => void
  onCreateFolder: (name: string, parentId?: string) => void
  onRenameFolder: (id: string, name: string) => void
  onDeleteFolder: (id: string) => void
  onDropDocument: (documentId: string, folderId: string | null) => void
}

const FolderItem = ({
  folder,
  depth,
  allFolders,
  selectedFolderId,
  draggedDocumentId,
  dragOverFolderId,
  setDragOverFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onDropDocument,
}: FolderItemProps) => {
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(folder.name)
  const children = allFolders.filter((f) => f.parentFolderId === folder.id)
  const isSelected = selectedFolderId === folder.id

  const commitRename = () => {
    const trimmed = renameValue.trim()
    if (trimmed && trimmed !== folder.name) onRenameFolder(folder.id, trimmed)
    setRenaming(false)
  }

  return (
    <div className={styles.folderItem} style={{ paddingLeft: depth * 12 }}>
      <div
        className={`${styles.folderRow} ${isSelected ? styles.folderRowSelected : ''} ${draggedDocumentId ? styles.folderRowDropTarget : ''} ${dragOverFolderId === folder.id ? styles.folderRowDragOver : ''}`}
        onClick={() => onSelectFolder(folder.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onSelectFolder(folder.id)}
        onDragOver={(e) => {
          if (!draggedDocumentId) return
          e.preventDefault()
          setDragOverFolderId(folder.id)
        }}
        onDragLeave={() => {
          if (dragOverFolderId === folder.id) {
            setDragOverFolderId(null)
          }
        }}
        onDrop={(e) => {
          if (!draggedDocumentId) return
          e.preventDefault()
          setDragOverFolderId(null)
          onDropDocument(draggedDocumentId, folder.id)
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className={styles.folderIcon}>
          <path
            d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
        </svg>

        {renaming ? (
          <input
            className={styles.renameInput}
            value={renameValue}
            autoFocus
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename()
              if (e.key === 'Escape') setRenaming(false)
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={styles.folderName} onDoubleClick={() => setRenaming(true)}>
            {folder.name}
          </span>
        )}

        <div className={styles.folderActions}>
          <button
            type="button"
            title="Add subfolder"
            className={styles.actionBtn}
            onClick={(e) => {
              e.stopPropagation()
              const name = window.prompt('Subfolder name:')
              if (name?.trim()) onCreateFolder(name.trim(), folder.id)
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button
            type="button"
            title="Delete folder"
            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
            onClick={(e) => {
              e.stopPropagation()
              if (window.confirm(`Delete folder "${folder.name}"?`)) onDeleteFolder(folder.id)
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M6 7h12M10 11v6M14 11v6M9 7V5h6v2M5 7l1 13h12l1-13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {children.length > 0 && (
        <div>
          {children.map((child) => (
            <FolderItem
              key={child.id}
              folder={child}
              depth={depth + 1}
              allFolders={allFolders}
              selectedFolderId={selectedFolderId}
              draggedDocumentId={draggedDocumentId}
              dragOverFolderId={dragOverFolderId}
              setDragOverFolderId={setDragOverFolderId}
              onSelectFolder={onSelectFolder}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onDropDocument={onDropDocument}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const WorkspaceFolderTree = ({
  folders,
  selectedFolderId,
  draggedDocumentId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onDropDocument,
}: WorkspaceFolderTreeProps) => {
  const rootFolders = folders.filter((f) => !f.parentFolderId)
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null)

  return (
    <div className={styles.tree}>
      <button
        type="button"
        className={`${styles.allDocsBtn} ${selectedFolderId === null ? styles.allDocsBtnActive : ''} ${draggedDocumentId ? styles.allDocsBtnDropTarget : ''} ${draggedDocumentId && dragOverFolderId === 'root' ? styles.allDocsBtnDragOver : ''}`}
        onClick={() => onSelectFolder(null)}
        onDragOver={(e) => {
          if (!draggedDocumentId) return
          e.preventDefault()
          setDragOverFolderId('root')
        }}
        onDragLeave={() => {
          if (dragOverFolderId === 'root') {
            setDragOverFolderId(null)
          }
        }}
        onDrop={(e) => {
          if (!draggedDocumentId) return
          e.preventDefault()
          setDragOverFolderId(null)
          onDropDocument(draggedDocumentId, null)
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M4 6h16M4 10h16M4 14h10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
        All Documents
      </button>

      <div className={styles.treeSection}>
        <div className={styles.treeSectionHeader}>
          <span>Folders</span>
          <button
            type="button"
            className={styles.addFolderBtn}
            title="New folder"
            onClick={() => {
              const name = window.prompt('Folder name:')
              if (name?.trim()) onCreateFolder(name.trim())
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {rootFolders.length === 0 ? (
          <p className={styles.emptyHint}>No folders yet.</p>
        ) : (
          rootFolders.map((folder) => (
            <FolderItem
              key={folder.id}
              folder={folder}
              depth={0}
              allFolders={folders}
              selectedFolderId={selectedFolderId}
              draggedDocumentId={draggedDocumentId}
              dragOverFolderId={dragOverFolderId}
              setDragOverFolderId={setDragOverFolderId}
              onSelectFolder={onSelectFolder}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onDropDocument={onDropDocument}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default WorkspaceFolderTree
